import { atom } from 'infrastructure/state/jotai.wrapper'
import { createIdMap, NodeIdMap } from '../../version_mapping/utility'
import { AllUpdatesForNodes, UpdatesByType, XRefRootNode } from './types'
import { INode } from 'shared/types/tree'
import {
  getAllXRefNodes,
  getNodeRefUpdatesForNodes,
  getXRefRoots,
  matchNodes,
} from './utility'
import { DeleteNodeUpdateData, UpdateNodeData } from '../nodeUpdates/types'
import { idsForNode, UpdateMethod, UpdateType } from '../nodeUpdates/utility'
import { allProducts, allProductsById } from 'shared/api/masters'
import { allVanityUrlsByProductId } from 'shared/api/vanityUrls'

export const globalMessageAtom = atom('')

export const doingUpdatesAtom = atom(false)

export const crossAssociationTreeAtom = atom<INode | undefined>(undefined)

export const productIdAtom = atom(0)

export const subTreeAtom = atom<XRefRootNode[]>((get) => {
  const tree = get(crossAssociationTreeAtom)
  if (!tree) return []
  return getXRefRoots(tree)
})

export const subTreeNodeIdMappingAtom = atom<[string, string][]>((get) => {
  const [tree1, tree2] = get(subTreeAtom)
  if (!tree1 || !tree2) return []
  return matchNodes(tree1, tree2)
})

export const productAtom = atom(async (get) => {
  const productId = get(productIdAtom)
  const productsById = await get(allProductsById)
  const tree = get(crossAssociationTreeAtom)
  const products = await get(allProducts)

  if (productId) {
    return productsById[productId]
  }

  if (!products) return

  if (tree) {
    return products.find(({ editable_tree, primary_tree_tree_id }) => {
      if (primary_tree_tree_id === tree.tree_id) return true
      return editable_tree?.tree_id === tree.tree_id
    })
  }
})

export const vanityIdAtom = atom(async (get) => {
  const product = await get(productAtom)
  const vanityIds = await get(allVanityUrlsByProductId)
  if (!product) return

  return vanityIds[product.id]?.URL
})

export const allUpdatesByNodeIdAtom = atom<Promise<AllUpdatesForNodes>>(
  async (get) => {
    const mapping = get(subTreeNodeIdMappingAtom)
    const vanityId = await get(vanityIdAtom)
    const tree = get(crossAssociationTreeAtom)
    if (!tree || !vanityId) return {}
    return mapping.reduce<AllUpdatesForNodes>((acc, mapping) => {
      return {
        ...acc,
        ...getNodeRefUpdatesForNodes(mapping, vanityId, tree),
      }
    }, {})
  },
)

function hasUpdates(updates: UpdatesByType) {
  return !!Object.values(updates).find((l) => l.length)
}

export const needingUpdateMatches = atom(async (get) => {
  const matches = get(subTreeNodeIdMappingAtom)
  const updates = await get(allUpdatesByNodeIdAtom)
  return matches.filter((match) => {
    return match.find((nid) => hasUpdates(updates[nid]))
  })
})

export const allUpdatesAtom = atom(async (get) => {
  const updates = await get(allUpdatesByNodeIdAtom)
  return Object.values(updates).reduce<UpdateNodeData[]>((acc, nodeUpdates) => {
    const { updates, deletes, creates } = nodeUpdates
    return [...acc, ...updates, ...creates, ...deletes]
  }, [])
})

export const allDeletesAtom = atom((get) => {
  const subtrees = get(subTreeAtom)
  return subtrees.reduce<DeleteNodeUpdateData[]>((acc, tree) => {
    return [
      ...acc,
      ...getAllXRefNodes(tree).map<DeleteNodeUpdateData>((n) => ({
        type: UpdateType.node,
        method: UpdateMethod.delete,
        ids: idsForNode(n),
      })),
    ]
  }, [])
})
